import { Button } from '@emailforge/ui';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@/components/ui/icons';
import {
  type TrackingDefaults,
  useUpdateTemplateMutation,
} from '@/features/templates/templatesApi';

/**
 * Popover trigger for the editor toolbar. Lets the template owner opt in or
 * out of open + click tracking as the default for every send dispatched from
 * this template. The send API can still override per-call.
 *
 *   `null` defaultTracking on the row means "use the platform default" which
 *   is currently `{ open: true, click: true }`. We surface that visually
 *   by showing both switches as on while disabled until the user explicitly
 *   sets a custom value.
 */

type Props = {
  templateId: string;
  current: TrackingDefaults | null;
};

export function TrackingDefaultsControl({ templateId, current }: Props) {
  const [open, setOpen] = useState(false);
  const [updateTemplate, { isLoading }] = useUpdateTemplateMutation();
  const popoverRef = useRef<HTMLDivElement>(null);

  // Local mirror so toggling feels instant; the mutation persists in the
  // background and the cached query re-hydrates on success.
  const [local, setLocal] = useState<TrackingDefaults>(
    current ?? { open: true, click: true },
  );
  useEffect(() => {
    setLocal(current ?? { open: true, click: true });
  }, [current?.open, current?.click]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  const persist = async (next: TrackingDefaults | null) => {
    await updateTemplate({ id: templateId, patch: { defaultTracking: next } }).unwrap();
  };

  const isCustomized = current !== null;
  const summary = current
    ? current.open && current.click
      ? 'Tracking: on'
      : current.open || current.click
        ? 'Tracking: partial'
        : 'Tracking: off'
    : 'Tracking: default';

  return (
    <div ref={popoverRef} style={{ position: 'relative' }}>
      <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}
        title="Default tracking for sends from this template"
      >
        <Icon.eye size={13} /> {summary}
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label="Default tracking"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 280,
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-3)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
            padding: 14,
            zIndex: 30,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Default tracking</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 12 }}>
            Applied to every send from this template unless the API call sets its own
            <span className="mono" style={{ marginInline: 4 }}>tracking</span>field.
          </div>

          <Toggle
            label="Open tracking"
            description="Embeds a 1×1 pixel before </body>"
            checked={local.open}
            disabled={isLoading}
            onChange={async (v) => {
              const next = { ...local, open: v };
              setLocal(next);
              await persist(next);
            }}
          />
          <div style={{ height: 8 }} />
          <Toggle
            label="Click tracking"
            description="Rewrites <a href> through /t/c/<token>"
            checked={local.click}
            disabled={isLoading}
            onChange={async (v) => {
              const next = { ...local, click: v };
              setLocal(next);
              await persist(next);
            }}
          />

          {isCustomized && (
            <Button variant="ghost" size="sm" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
              disabled={isLoading}
              onClick={async () => {
                await persist(null);
                setLocal({ open: true, click: true });
              }}
            >
              Reset to platform default
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Toggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        cursor: disabled ? 'wait' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.4 }}>{description}</div>
      </div>
    </label>
  );
}
